/* Farmers Stop: one semantic search per submitted query; no secret in theme code. */
(() => {
  'use strict';
  const ENDPOINT = 'https://app.farmersstop.com/ai-search.php';
  class FarmersStopAiResults {
    constructor(root) {
      this.root = root;
      this.query = (root.dataset.query || '').trim();
      this.tools = root.querySelector('[data-fs-ai-tools]');
      this.status = root.querySelector('[data-fs-ai-status]');
      this.panel = root.querySelector('[data-fs-ai-panel]');
      this.grid = root.querySelector('[data-fs-ai-grid]');
      this.standard = root.querySelector('[data-fs-standard-results]');
      this.buttons = root.querySelectorAll('[data-fs-search-mode]');
      this.loaded = false;
      this.controller = null;
      this.mode = 'standard';
      if (!this.query || !this.tools || !this.panel || !this.grid || !this.standard || !this.status) return;
      this.standard.querySelectorAll('.pagination a[href]').forEach(link => {
        const url = new URL(link.getAttribute('href'), location.href);
        if (url.origin === location.origin) { url.searchParams.set('fs_search', 'standard'); link.href = url.href; }
      });
      this.tools.hidden = false;
      this.buttons.forEach(button => button.addEventListener('click', () => this.choose(button.dataset.fsSearchMode, true)));
      root.querySelector('[data-fs-show-standard]')?.addEventListener('click', () => this.choose('standard', true));
      const url = new URL(location.href);
      const isLaterPage = Number(url.searchParams.get('page') || 1) > 1;
      const hasNativeFilters = Array.from(url.searchParams.keys()).some(key => key.startsWith('filter.'));
      this.choose(url.searchParams.get('fs_search') === 'standard' || isLaterPage || hasNativeFilters ? 'standard' : 'ai', false);
    }
    message(text) {
      this.status.textContent = text;
      this.status.hidden = !text;
    }
    markMode(mode) {
      this.buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fsSearchMode === mode)));
    }
    remember(mode) {
      try {
        const url = new URL(location.href);
        if (mode === 'standard') url.searchParams.set('fs_search', 'standard');
        else { url.searchParams.delete('fs_search'); url.searchParams.delete('page'); }
        history.replaceState(history.state, '', url.href);
      } catch { /* Searching still works when a WebView restricts history access. */ }
    }
    choose(mode, remember) {
      this.mode = mode === 'standard' ? 'standard' : 'ai';
      this.markMode(this.mode);
      if (remember) this.remember(this.mode);
      if (this.mode === 'standard') {
        this.controller?.abort();
        this.controller = null;
        this.panel.hidden = true;
        this.standard.hidden = false;
        this.message('');
        this.root.removeAttribute('aria-busy');
      } else if (this.loaded) {
        this.panel.hidden = false;
        this.standard.hidden = true;
        this.message('');
      } else if (!this.controller) this.load();
    }
    async load() {
      if (new TextEncoder().encode(this.query).length > 240) {
        this.fallback('This query is too long for AI search. Showing standard results.');
        return;
      }
      const controller = new AbortController();
      this.controller = controller;
      const timer = setTimeout(() => controller.abort(), 60000);
      this.root.setAttribute('aria-busy', 'true');
      this.message('Finding AI matches… You can also browse standard search.');
      // Native results stay available while AI and same-origin product cards load.
      this.standard.hidden = false;
      try {
        if (this.root.dataset.endpoint !== ENDPOINT) throw new Error('Invalid endpoint.');
        const url = new URL(ENDPOINT);
        url.searchParams.set('q', this.query);
        const response = await fetch(url.href, { signal: controller.signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
        if (!response.ok) throw new Error(`AI HTTP ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data.products)) throw new Error('Invalid AI response.');
        if (this.controller !== controller || this.mode !== 'ai' || !this.root.isConnected) return;
        const handles = [];
        for (const product of data.products) {
          if (!product || typeof product.handle !== 'string' || !/^[a-z0-9-]+$/.test(product.handle)) continue;
          const link = new URL(product.url);
          if (link.origin !== 'https://www.farmersstop.com' || link.pathname !== '/products/' + product.handle || link.username || link.password) continue;
          if (!handles.includes(product.handle)) handles.push(product.handle);
        }
        if (!handles.length) {
          if (this.controller === controller) this.fallback('No close AI matches. Showing standard search results.');
          return;
        }
        const cardsURL = new URL(location.href);
        // Preserve locale, preview_theme_id, currency and theme preview cookies.
        cardsURL.searchParams.set('type', 'product');
        cardsURL.searchParams.set('view', 'fs-ai-cards');
        cardsURL.searchParams.delete('page');
        cardsURL.searchParams.delete('sort_by');
        cardsURL.searchParams.delete('fs_search');
        for (const key of Array.from(cardsURL.searchParams.keys())) if (key.startsWith('filter.')) cardsURL.searchParams.delete(key);
        const wrapper = document.createElement('div');
        // Render in small batches within Liquid's unique all_products limit.
        for (let start = 0; start < handles.length; start += 16) {
          cardsURL.searchParams.set('q', handles.slice(start, start + 16).join('|'));
          const cardsResponse = await fetch(cardsURL.href, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' }, cache: 'no-store' });
          if (!cardsResponse.ok) throw new Error('Product cards unavailable.');
          const html = await cardsResponse.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const batch = doc.querySelector('[data-fs-ai-card-response]');
          if (!batch) throw new Error('AI product-card template unavailable.');
          wrapper.append(...Array.from(batch.children));
        }
        if (this.controller !== controller || this.mode !== 'ai' || !this.root.isConnected) return;
        const fragment = document.createDocumentFragment();
        let count = 0;
        // Keep the AI order, and accept only server-rendered cards requested above.
        for (const handle of handles) {
          const slot = Array.from(wrapper.children).find(child => child.dataset.fsAiCardHandle === handle && child.querySelector('.product-card'));
          if (!slot) continue;
          slot.querySelectorAll('script,iframe,object,embed,form').forEach(node => node.remove());
          slot.querySelectorAll('*').forEach(node => {
            for (const attribute of Array.from(node.attributes)) if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name);
          });
          fragment.append(document.importNode(slot, true)); count++;
        }
        if (!count) { this.fallback('No available AI product cards. Showing standard search results.'); return; }
        this.grid.replaceChildren(fragment);
        this.root.querySelector('[data-fs-ai-count]').textContent = `${count} ${count === 1 ? 'product' : 'products'}`;
        this.loaded = true;
        this.panel.hidden = false;
        this.standard.hidden = true;
        this.message('');
        document.dispatchEvent(new CustomEvent('farmersstop:products:loaded', { detail: { container: this.grid } }));
      } catch {
        if (this.controller === controller && this.mode === 'ai') this.fallback('AI search is temporarily unavailable. Showing standard search results.');
      } finally {
        clearTimeout(timer);
        if (this.controller === controller) { this.controller = null; this.root.removeAttribute('aria-busy'); }
      }
    }
    fallback(text) {
      this.mode = 'standard';
      this.markMode('standard');
      this.panel.hidden = true;
      this.standard.hidden = false;
      this.message(text);
      this.root.removeAttribute('aria-busy');
    }
    destroy() { this.controller?.abort(); this.controller = null; }
  }
  function initialize(scope = document) {
    const roots = scope.matches?.('[data-fs-ai-search-page]') ? [scope] : scope.querySelectorAll('[data-fs-ai-search-page]');
    roots.forEach(root => {
      if (!root.fsAiSearch) root.fsAiSearch = new FarmersStopAiResults(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-fs-ai-search-page]').forEach(root => root.fsAiSearch?.destroy());
  });
})();
